import axios from "axios";
import {
  buildClientOfferBatchHtmlDraft,
  buildClientOfferHtmlDraft,
  resolveOfferUnitPrice,
} from "./clientOfferEmailHtml";

export {
  buildClientOfferBatchHtmlDraft,
  buildClientOfferHtmlDraft,
  resolveOfferUnitPrice,
};

const API_URL = process.env.REACT_APP_API_URL;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

export async function getUniboxAccounts() {
  const { data } = await axios.get(`${API_URL}/api/unibox/accounts`, {
    headers: authHeaders(),
  });
  return Array.isArray(data) ? data : [];
}

export async function getEstimateRfqs(presupuestoId) {
  const { data } = await axios.get(
    `${API_URL}/api/presupuestos/${presupuestoId}/rfqs`,
    { headers: authHeaders() },
  );
  return Array.isArray(data) ? data : [];
}

export async function getEstimateCommunications(presupuestoId, options = {}) {
  const sync = options.sync === false || options.sync === 0 ? "0" : "1";
  const { data } = await axios.get(
    `${API_URL}/api/presupuestos/${presupuestoId}/communications`,
    {
      headers: authHeaders(),
      params: { sync },
    },
  );
  return {
    communications: Array.isArray(data?.communications)
      ? data.communications
      : [],
    rfqs: Array.isArray(data?.rfqs) ? data.rfqs : [],
    sync: data?.sync || null,
  };
}

export async function syncEstimateCommunications(presupuestoId) {
  const { data } = await axios.post(
    `${API_URL}/api/presupuestos/${presupuestoId}/communications/sync`,
    {},
    { headers: authHeaders() },
  );
  return data;
}

export async function getEstimateCommBody(commId) {
  const { data } = await axios.get(
    `${API_URL}/api/estimate-comms/${commId}/body`,
    { headers: authHeaders() },
  );
  return data;
}

export function getEstimateCommAttachmentDownloadUrl(commId, attachmentId) {
  const id = String(attachmentId ?? "");
  // Query param evita doble-encode de ids virtuales v:mid%40x::inline%3AN
  const qs = new URLSearchParams({ id });
  return `${API_URL}/api/estimate-comms/${commId}/attachments/download?${qs.toString()}`;
}

export async function downloadEstimateCommAttachment(
  commId,
  attachmentId,
  filename = "adjunto",
) {
  const { data } = await axios.get(
    getEstimateCommAttachmentDownloadUrl(commId, attachmentId),
    {
      headers: authHeaders(),
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "adjunto";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** Blob del adjunto (para miniatura / preview). */
export async function fetchEstimateCommAttachmentBlob(commId, attachmentId) {
  const { data, headers } = await axios.get(
    getEstimateCommAttachmentDownloadUrl(commId, attachmentId),
    {
      headers: authHeaders(),
      responseType: "blob",
    },
  );
  const contentType =
    headers?.["content-type"] ||
    headers?.["Content-Type"] ||
    data?.type ||
    "application/octet-stream";
  return data instanceof Blob
    ? data.type
      ? data
      : new Blob([data], { type: contentType })
    : new Blob([data], { type: contentType });
}

/** mode: 'unlink' | 'trash' */
export async function deleteEstimateComm(commId, mode = "unlink") {
  const { data } = await axios.delete(
    `${API_URL}/api/estimate-comms/${commId}`,
    {
      headers: authHeaders(),
      params: { mode },
    },
  );
  return data;
}

/** mode: 'unlink' | 'trash' */
export async function deleteEstimateRfq(rfqId, mode = "unlink") {
  const { data } = await axios.delete(`${API_URL}/api/rfqs/${rfqId}`, {
    headers: authHeaders(),
    params: { mode },
  });
  return data;
}

export async function getEstimatesCommsSummary() {
  const { data } = await axios.get(
    `${API_URL}/api/presupuestos/comms-summary`,
    { headers: authHeaders() },
  );
  return Array.isArray(data) ? data : [];
}

/** Contadores + lista RFQ para widget Mi Dashboard. */
export async function getRfqDashboardSummary() {
  const { data } = await axios.get(`${API_URL}/api/rfqs/dashboard-summary`, {
    headers: authHeaders(),
  });
  return {
    counts: data?.counts || {
      pedidas: 0,
      pendientesContestar: 0,
      contestadas: 0,
      pendientesAsignar: 0,
      byEstado: { Sent: 0, Quoted: 0, Applied: 0 },
    },
    items: Array.isArray(data?.items) ? data.items : [],
  };
}

export async function createAndSendEstimateRfq(
  presupuestoId,
  payload,
  files = [],
) {
  const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
  if (!fileList.length) {
    const { data } = await axios.post(
      `${API_URL}/api/presupuestos/${presupuestoId}/rfqs`,
      payload,
      { headers: authHeaders() },
    );
    return data;
  }

  const form = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (typeof value === "object") {
      form.append(key, JSON.stringify(value));
    } else {
      form.append(key, String(value));
    }
  });
  fileList.forEach((file) => form.append("attachments", file));

  const { data } = await axios.post(
    `${API_URL}/api/presupuestos/${presupuestoId}/rfqs`,
    form,
    { headers: authHeaders() },
  );
  return data;
}

/** Cliente Tommy Hilfiger (TH): al ofertar no se envía email. */
export const TOMMY_CLIENT_ID = 27;

export function isTommyClient({ idCliente, clienteNombre, name } = {}) {
  const id = Number(idCliente);
  if (Number.isFinite(id) && id === TOMMY_CLIENT_ID) return true;
  const n = String(clienteNombre || name || "")
    .trim()
    .toUpperCase();
  if (!n) return false;
  return n.includes("TOMMY");
}

/** Marca presupuesto como Offered (sin enviar email). */
export async function markPresupuestoOffered(presupuestoId) {
  const fecha = new Date().toISOString();
  const { data } = await axios.put(
    `${API_URL}/api/presupuestos/${presupuestoId}/estado`,
    { estado: "Offered", FechaPresentacion: fecha },
    { headers: authHeaders() },
  );
  return {
    ...(data && typeof data === "object" ? data : {}),
    presupuesto: {
      Estado: "Offered",
      FechaPresentacion: fecha,
      ...(data?.presupuesto || {}),
    },
  };
}

/** Envía oferta al cliente; si markOffered, marca Offered tras el send. */
export async function sendEstimateClientOffer(
  presupuestoId,
  payload,
  files = [],
) {
  const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
  const form = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (typeof value === "object") {
      form.append(key, JSON.stringify(value));
    } else {
      form.append(key, String(value));
    }
  });
  fileList.forEach((file) => {
    if (file) form.append("attachments", file);
  });

  const { data } = await axios.post(
    `${API_URL}/api/presupuestos/${presupuestoId}/client-offers/send`,
    form,
    { headers: authHeaders() },
  );
  return data;
}

export function buildClientOfferSubject({
  cliente,
  modelo,
  numeroPresupuesto,
  lang = "en",
} = {}) {
  const client = String(cliente || "").trim() || "—";
  const model = String(modelo || "").trim() || "—";
  const num = String(numeroPresupuesto || "").trim() || "P-?";
  const tail = lang === "es" ? "oferta" : "quotation";
  return `${client} | ${model} | ${num} | ${tail}`;
}

export function buildClientOfferBatchSubject({
  cliente,
  temporada,
  count,
  lang = "en",
} = {}) {
  const client = String(cliente || "").trim() || "—";
  const n = Number(count) || 0;
  const styles =
    lang === "es"
      ? `${n} estilo${n === 1 ? "" : "s"}`
      : `${n} style${n === 1 ? "" : "s"}`;
  const season = String(temporada || "").trim();
  const tail = lang === "es" ? "oferta" : "quotation";
  return season
    ? `${client} | ${season} | ${styles} | ${tail}`
    : `${client} | ${styles} | ${tail}`;
}

export async function sendEstimateClientOfferBatch(payload, files = []) {
  const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
  const form = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (typeof value === "object") {
      form.append(key, JSON.stringify(value));
    } else {
      form.append(key, String(value));
    }
  });
  fileList.forEach((file) => {
    if (file) form.append("attachments", file);
  });

  const { data } = await axios.post(
    `${API_URL}/api/presupuestos/client-offers/send-batch`,
    form,
    { headers: authHeaders() },
  );
  return data;
}

export async function replyEstimateCommunication(
  presupuestoId,
  payload,
  files = [],
) {
  const form = new FormData();
  Object.entries(payload || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    form.append(key, String(value));
  });
  (Array.isArray(files) ? files : []).forEach((file) => {
    if (file) form.append("attachments", file);
  });

  const { data } = await axios.post(
    `${API_URL}/api/presupuestos/${presupuestoId}/communications/reply`,
    form,
    { headers: authHeaders() },
  );
  return data;
}

export async function updateEstimateRfq(rfqId, payload) {
  const { data } = await axios.patch(`${API_URL}/api/rfqs/${rfqId}`, payload, {
    headers: authHeaders(),
  });
  return data;
}

export async function applyEstimateRfqCost(rfqId, payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/rfqs/${rfqId}/apply-cost`,
    payload,
    { headers: authHeaders() },
  );
  return data;
}

export async function insertEstimateRfqQuoteLines(rfqId, payload = {}) {
  const { data } = await axios.post(
    `${API_URL}/api/rfqs/${rfqId}/insert-quote-lines`,
    payload,
    { headers: authHeaders() },
  );
  return data;
}

export function resolvePresupuestoTemporada(presupuesto) {
  if (!presupuesto) return "";
  const candidates = [
    presupuesto.TemporadaNombre,
    presupuesto.TEMPORADA,
    presupuesto.temporadaNombre,
    // Temporada en presupuesto a veces es ID numérico (no usable como texto)
    (() => {
      const raw = presupuesto.Temporada;
      if (raw == null || raw === "") return "";
      const s = String(raw).trim();
      if (/^\d+$/.test(s)) return "";
      return s;
    })(),
  ];
  for (const c of candidates) {
    const s = String(c || "").trim();
    if (s) return s;
  }
  return "";
}

export function buildRfqSubject(numeroPresupuesto, lang = "en", opts = {}) {
  const num = String(numeroPresupuesto || "").trim() || "P-?";
  const style = String(
    opts.style || opts.diseno || opts.DiseñoMuestra || opts.designCliente || "",
  ).trim();
  const stylePart = style ? ` ${style} |` : "";
  if (lang === "es") return `[RFQ ${num}]${stylePart} Solicitud de coste`;
  return `[RFQ ${num}]${stylePart} Cost quotation request`;
}

const RFQ_I18N = {
  es: {
    hello: "Hola,",
    intro:
      "Te solicitamos cotización de coste para el siguiente presupuesto de Stage Design.",
    presupuesto: "Presupuesto",
    nombre: "Nombre",
    unidades: "Unidades",
    temporada: "Temporada",
    diseno: "Diseño",
    proveedor: "Proveedor",
    deadline: "Fecha límite respuesta",
    detailsTitle: "Detalle RFQ",
    sizes: "Tallas (Sizes)",
    brandedTrims: "Branded trims (nominated / local)",
    labels: "Labels (nominated / local)",
    fabricsTest: "Fabrics test",
    garmentTest: "Garment test",
    finalInspection: "Inspección final (3rd party / in-house)",
    auditedFactory: "Audited factory",
    poDate: "PO date",
    exFd: "ExFD",
    notes: "Notas",
    closing:
      "Usa el formulario online del enlace del email (preferible) o responde indicando coste unitario, moneda, lead time y validez.",
    thanks: "Gracias.",
  },
  en: {
    hello: "Hello,",
    intro:
      "We kindly request a cost quotation for the following Stage Design estimate.",
    presupuesto: "Estimate",
    nombre: "Name",
    unidades: "Units",
    temporada: "Season",
    diseno: "Design",
    proveedor: "Supplier",
    deadline: "Reply deadline",
    detailsTitle: "RFQ details",
    sizes: "Sizes",
    brandedTrims: "Branded trims nominated or local",
    labels: "Labels nominated or local",
    fabricsTest: "Fabrics test",
    garmentTest: "Garment test",
    finalInspection: "3rd party or in-house final inspection",
    auditedFactory: "Audited factory",
    poDate: "PO date",
    exFd: "ExFD",
    notes: "Notes",
    closing:
      "Please use the online form link in this email (preferred), or reply with unit cost, currency, lead time and offer validity.",
    thanks: "Thank you.",
  },
};

function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function rowHtml(label, value) {
  const v = String(value ?? "").trim();
  if (!v) return "";
  return `<tr><td style="padding:6px 10px;border:1px solid #ddd;font-weight:600;vertical-align:top">${escapeHtml(
    label,
  )}</td><td style="padding:6px 10px;border:1px solid #ddd">${escapeHtml(
    v,
  ).replace(/\n/g, "<br/>")}</td></tr>`;
}

export function buildRfqHtmlDraft({
  lang = "en",
  numeroPresupuesto,
  nombrePresupuesto,
  units,
  temporada,
  diseno,
  proveedorNombre,
  deadline,
  detailFields = {},
  notas,
}) {
  const t = RFQ_I18N[lang] || RFQ_I18N.en;
  const d = detailFields || {};

  const headerRows = [
    rowHtml(t.presupuesto, numeroPresupuesto),
    rowHtml(t.nombre, nombrePresupuesto),
    rowHtml(t.unidades, units != null && units !== "" ? units : ""),
    rowHtml(t.temporada, temporada),
    rowHtml(t.diseno, diseno),
    rowHtml(t.proveedor, proveedorNombre),
    rowHtml(t.deadline, deadline),
  ]
    .filter(Boolean)
    .join("");

  const detailRows = [
    rowHtml(t.sizes, d.sizes),
    rowHtml(t.brandedTrims, d.brandedTrims),
    rowHtml(t.labels, d.labels),
    rowHtml(t.fabricsTest, d.fabricsTest),
    rowHtml(t.garmentTest, d.garmentTest),
    rowHtml(t.finalInspection, d.finalInspection),
    rowHtml(t.auditedFactory, d.auditedFactory),
    rowHtml(t.poDate, d.poDate),
    rowHtml(t.exFd, d.exFd),
  ]
    .filter(Boolean)
    .join("");

  const notesBlock = String(notas || "").trim()
    ? `<p style="margin-top:16px"><strong>${escapeHtml(
        t.notes,
      )}:</strong><br/>${escapeHtml(notas).replace(/\n/g, "<br/>")}</p>`
    : "";

  const detailsBlock = detailRows
    ? `<p style="margin:16px 0 8px"><strong>${escapeHtml(
        t.detailsTitle,
      )}</strong></p><table style="border-collapse:collapse;margin:0 0 12px">${detailRows}</table>`
    : "";

  return `
<p>${escapeHtml(t.hello)}</p>
<p>${escapeHtml(t.intro)}</p>
<table style="border-collapse:collapse;margin:12px 0">${headerRows}</table>
${detailsBlock}
${notesBlock}
<p>${t.closing}</p>
<p>${escapeHtml(t.thanks)}</p>
`.trim();
}
